/**
 * F2.2b / F2.3b / F2.5a2 — Validation + mapping formulaire → API catalogue.
 * Types supportés : REDUCTION_EUR | REDUCTION_POURCENT.
 */

export const OFFER_KIND_EUR = 'EUR';
export const OFFER_KIND_POURCENT = 'POURCENT';

export const EFFECT_REDUCTION_EUR = 'REDUCTION_EUR';
export const EFFECT_REDUCTION_POURCENT = 'REDUCTION_POURCENT';

export const PERSISTED_STATUTS = new Set(['BROUILLON', 'ACTIVE', 'SUSPENDUE', 'ARCHIVEE']);
export const WRITABLE_STATUTS = new Set(['BROUILLON', 'ACTIVE', 'SUSPENDUE']);

export function emptyRecompenseForm(overrides = {}) {
  return {
    offerKind: OFFER_KIND_EUR,
    libelle: '',
    description: '',
    coutPoints: '',
    valeurEuros: '',
    valeurPourcent: '',
    validFrom: '',
    validTo: '',
    statut: 'BROUILLON',
    typeLocked: false,
    ...overrides,
  };
}

function effectsOf(recompense) {
  if (!recompense || typeof recompense !== 'object') return [];
  if (Array.isArray(recompense.effets)) return recompense.effets;
  if (Array.isArray(recompense.effects)) return recompense.effects;
  return [];
}

export function detectOfferKindFromRecompense(recompense) {
  const effects = effectsOf(recompense);
  const first = effects[0];
  if (first?.type === EFFECT_REDUCTION_POURCENT) return OFFER_KIND_POURCENT;
  return OFFER_KIND_EUR;
}

export function formFromRecompense(recompense) {
  if (!recompense || typeof recompense !== 'object') return emptyRecompenseForm();
  const effects = effectsOf(recompense);
  const eur = effects.find((e) => e && e.type === EFFECT_REDUCTION_EUR);
  const pct = effects.find((e) => e && e.type === EFFECT_REDUCTION_POURCENT);
  const offerKind = pct ? OFFER_KIND_POURCENT : OFFER_KIND_EUR;
  const toDateInput = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };
  return emptyRecompenseForm({
    offerKind,
    typeLocked: true,
    libelle: recompense.libelle || '',
    description: recompense.description || '',
    coutPoints: recompense.coutPoints != null ? String(recompense.coutPoints) : '',
    valeurEuros: eur?.valeur != null ? String(eur.valeur) : '',
    valeurPourcent: pct?.valeur != null ? String(pct.valeur) : '',
    validFrom: toDateInput(recompense.validFrom),
    validTo: toDateInput(recompense.validTo),
    statut: PERSISTED_STATUTS.has(recompense.statut) ? recompense.statut : 'BROUILLON',
  });
}

function parsePositiveInt(raw) {
  if (raw === '' || raw == null) return { ok: false, value: null };
  const n = Number(raw);
  if (!Number.isFinite(n) || Number.isNaN(n) || !Number.isInteger(n) || n < 1) {
    return { ok: false, value: null };
  }
  return { ok: true, value: n };
}

function parsePositiveNumber(raw) {
  if (raw === '' || raw == null) return { ok: false, value: null };
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n) || Number.isNaN(n) || !(n > 0)) {
    return { ok: false, value: null };
  }
  return { ok: true, value: n };
}

function hasAtMostTwoDecimals(n) {
  if (!Number.isFinite(n)) return false;
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-8;
}

function parsePourcent(raw) {
  const parsed = parsePositiveNumber(raw);
  if (!parsed.ok) return { ok: false, value: null, reason: 'empty' };
  if (parsed.value > 100) return { ok: false, value: null, reason: 'max' };
  if (!hasAtMostTwoDecimals(parsed.value)) return { ok: false, value: null, reason: 'decimals' };
  return { ok: true, value: parsed.value };
}

/**
 * @returns {{ ok: boolean, errors: Record<string,string>, values?: object }}
 */
export function validateRecompenseForm(form) {
  const errors = {};
  const offerKind =
    form?.offerKind === OFFER_KIND_POURCENT ? OFFER_KIND_POURCENT : OFFER_KIND_EUR;

  const libelle = typeof form?.libelle === 'string' ? form.libelle.trim() : '';
  if (!libelle) errors.libelle = 'Indiquez un nom pour la récompense.';
  else if (libelle.length > 200) errors.libelle = 'Le nom est trop long (200 caractères max).';

  const pts = parsePositiveInt(form?.coutPoints);
  if (!pts.ok) errors.coutPoints = 'Indiquez un nombre de points entier supérieur à 0.';

  let valeurEuros = null;
  let valeurPourcent = null;
  if (offerKind === OFFER_KIND_POURCENT) {
    const pct = parsePourcent(form?.valeurPourcent);
    if (!pct.ok) {
      if (pct.reason === 'max') {
        errors.valeurPourcent = 'Le pourcentage ne peut pas dépasser 100.';
      } else if (pct.reason === 'decimals') {
        errors.valeurPourcent = 'Deux décimales maximum.';
      } else {
        errors.valeurPourcent = 'Indiquez un pourcentage supérieur à 0.';
      }
    } else {
      valeurPourcent = pct.value;
    }
  } else {
    const euros = parsePositiveNumber(form?.valeurEuros);
    if (!euros.ok) errors.valeurEuros = 'Indiquez un montant offert supérieur à 0.';
    else valeurEuros = euros.value;
  }

  const statut = form?.statut;
  if (!WRITABLE_STATUTS.has(statut) && statut !== 'ARCHIVEE') {
    errors.statut = 'État non reconnu.';
  }
  if (statut === 'PLANIFIEE' || statut === 'EXPIREE') {
    errors.statut = 'Planifiée et Expirée sont calculées automatiquement à partir des dates.';
  }

  const from = form?.validFrom ? String(form.validFrom).trim() : '';
  const to = form?.validTo ? String(form.validTo).trim() : '';
  if (from && to && new Date(from) > new Date(to)) {
    errors.validTo = 'La date de fin doit être postérieure ou égale à la date de début.';
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  const description =
    typeof form.description === 'string' && form.description.trim()
      ? form.description.trim()
      : null;

  return {
    ok: true,
    errors: {},
    values: {
      offerKind,
      libelle,
      description,
      coutPoints: pts.value,
      valeurEuros,
      valeurPourcent,
      validFrom: from || null,
      validTo: to || null,
      statut: statut === 'ARCHIVEE' ? 'ARCHIVEE' : statut,
    },
  };
}

/**
 * Mapping formulaire → payload API (jamais de conversion silencieuse % → EUR).
 */
export function buildRecompenseApiPayload(values, { forCreate = false } = {}) {
  const offerKind =
    values.offerKind === OFFER_KIND_POURCENT ? OFFER_KIND_POURCENT : OFFER_KIND_EUR;
  const effects =
    offerKind === OFFER_KIND_POURCENT
      ? [{ type: EFFECT_REDUCTION_POURCENT, valeur: values.valeurPourcent }]
      : [{ type: EFFECT_REDUCTION_EUR, valeur: values.valeurEuros }];

  const payload = {
    libelle: values.libelle,
    description: values.description,
    coutPoints: values.coutPoints,
    effects,
    eligibility: {},
    validFrom: values.validFrom,
    validTo: values.validTo,
  };
  if (values.statut && values.statut !== 'ARCHIVEE') {
    payload.statut = values.statut;
  } else if (forCreate) {
    payload.statut = 'BROUILLON';
  }
  return payload;
}

export function buildClientSpendPreview(coutPoints, valeurEuros) {
  const pts = parsePositiveInt(coutPoints);
  const euros = parsePositiveNumber(valeurEuros);
  if (!pts.ok || !euros.ok) return null;
  const e = formatAutoLibelleNumber(euros.value);
  return `Le client dépensera ${pts.value} point${pts.value > 1 ? 's' : ''} pour obtenir ${e} € sur sa commande.`;
}

export function buildClientSpendPreviewPourcent(coutPoints, valeurPourcent) {
  const pts = parsePositiveInt(coutPoints);
  const pct = parsePourcent(valeurPourcent);
  if (!pts.ok || !pct.ok) return null;
  const p = formatAutoLibelleNumber(pct.value);
  return `Le client dépensera ${pts.value} point${pts.value > 1 ? 's' : ''} pour obtenir ${p} % de réduction.`;
}

/** Résumé visuel création montant. */
export function buildMontantOfferSummary(coutPoints, valeurEuros) {
  const pts = parsePositiveInt(coutPoints);
  const euros = parsePositiveNumber(valeurEuros);
  if (!pts.ok || !euros.ok) return null;
  const e = formatAutoLibelleNumber(euros.value);
  return {
    points: pts.value,
    pointsLabel: pts.value === 1 ? '1 point' : `${pts.value} points`,
    offerLabel: `${e} € offerts`,
    eurosLabel: `${e} € offerts`,
  };
}

/** Résumé visuel création %. */
export function buildPourcentOfferSummary(coutPoints, valeurPourcent) {
  const pts = parsePositiveInt(coutPoints);
  const pct = parsePourcent(valeurPourcent);
  if (!pts.ok || !pct.ok) return null;
  const p = formatAutoLibelleNumber(pct.value);
  return {
    points: pts.value,
    pointsLabel: pts.value === 1 ? '1 point' : `${pts.value} points`,
    offerLabel: `${p} % de réduction`,
  };
}

/**
 * Affichage nombre pour auto-libellé (entier ou décimales FR avec virgule).
 */
export function formatAutoLibelleNumber(n) {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 100) / 100;
  let s = String(rounded);
  if (s.includes('e') || s.includes('E')) {
    s = rounded.toFixed(2);
  }
  if (s.includes('.')) {
    s = s.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
  }
  return s.replace('.', ',');
}

/** Patterns des libellés auto EUR / % (accepte . ou ,). */
const AUTO_LIBELLE_POURCENT_RE = /^(\d+(?:[.,]\d+)?)\s*%\s*de\s+réduction$/i;
const AUTO_LIBELLE_EUR_RE = /^(\d+(?:[.,]\d+)?)\s*€\s*offerts$/i;

/**
 * True si le libellé correspond à un auto-libellé généré (y compris stale).
 * Un libellé personnalisé (« OFFRE VIP ») retourne false.
 */
export function isAutoGeneratedLibelle(libelle) {
  if (typeof libelle !== 'string') return false;
  const t = libelle.trim();
  if (!t) return false;
  return AUTO_LIBELLE_POURCENT_RE.test(t) || AUTO_LIBELLE_EUR_RE.test(t);
}

export function defaultLibelleForOffer(offerKind, valeurEuros, valeurPourcent) {
  if (offerKind === OFFER_KIND_POURCENT) {
    const pct = parsePourcent(valeurPourcent);
    if (!pct.ok) return '';
    return `${formatAutoLibelleNumber(pct.value)} % de réduction`;
  }
  const euros = parsePositiveNumber(valeurEuros);
  if (!euros.ok) return '';
  return `${formatAutoLibelleNumber(euros.value)} € offerts`;
}

/**
 * Synchronise le libellé si vide ou encore auto (create + edit).
 * Ne touche jamais un libellé réellement personnalisé.
 * @returns {{ libelle: string, synced: boolean }}
 */
export function syncAutoLibelleIfNeeded(libelle, offerKind, valeurEuros, valeurPourcent) {
  const auto = defaultLibelleForOffer(offerKind, valeurEuros, valeurPourcent);
  if (!auto) {
    return { libelle: typeof libelle === 'string' ? libelle : '', synced: false };
  }
  const current = typeof libelle === 'string' ? libelle.trim() : '';
  if (!current || isAutoGeneratedLibelle(current)) {
    return { libelle: auto, synced: true };
  }
  return { libelle: current, synced: false };
}

/**
 * Aperçu statutAffiche dérivé (même logique F2.1 helpers, côté Admin).
 */
export function previewStatutAffiche(statut, validFrom, validTo, now = new Date()) {
  if (statut === 'BROUILLON' || statut === 'ARCHIVEE') return statut;
  const from = validFrom ? new Date(validFrom) : null;
  const to = validTo ? new Date(validTo) : null;
  if (statut === 'ACTIVE' && from && !Number.isNaN(from.getTime()) && now < from) {
    return 'PLANIFIEE';
  }
  if (
    (statut === 'ACTIVE' || statut === 'SUSPENDUE') &&
    to &&
    !Number.isNaN(to.getTime()) &&
    now > to
  ) {
    return 'EXPIREE';
  }
  return statut;
}

export function isMultiEurLiveBlockedError(err) {
  const code = err?.code || err?.response?.data?.code || err?.data?.code;
  return code === 'MULTI_EUR_LIVE_BLOCKED';
}

export function getErrorMessage(err, fallback = 'Une erreur est survenue') {
  return err?.response?.data?.error || err?.data?.error || err?.message || fallback;
}

export function getErrorStatus(err) {
  return err?.status || err?.response?.status || null;
}

/**
 * Construit la liste d’items ordre après déplacement ↑/↓.
 */
export function buildReorderItemsAfterMove(rows, id, direction) {
  const list = (rows || [])
    .filter((r) => r && r.id && r.statut !== 'ARCHIVEE')
    .slice()
    .sort((a, b) => {
      const oa = Number(a.ordre) || 0;
      const ob = Number(b.ordre) || 0;
      if (oa !== ob) return oa - ob;
      return String(a.id).localeCompare(String(b.id));
    });
  const idx = list.findIndex((r) => r.id === id);
  if (idx < 0) return null;
  const target = direction === 'up' ? idx - 1 : idx + 1;
  if (target < 0 || target >= list.length) return null;
  const next = list.slice();
  const tmp = next[idx];
  next[idx] = next[target];
  next[target] = tmp;
  return next.map((r, i) => ({ id: r.id, ordre: i }));
}
